import { Inject, Injectable } from '@angular/core';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  CalibrationType,
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  Fitter,
  getOperationType,
  OperationType,
  RoutingName,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { dayjs } from '@sc/utils';
import { Border, Buffer, Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import {
  AnySheetRow,
  DossierManagersSheetColumn,
  DossierManagersSheetColumnName,
  DossierManagersSheetRow,
  DossierSheetColumn,
  DossierSheetColumnName,
  DossierSheetRow,
  FileExporter,
  PartnerSheetColumn,
  PartnerSheetColumnName,
  PartnerSheetRow,
  SmsStatsSheetColumn,
  SmsStatsSheetRow,
  SpecificSheetColumn,
  SpecificSheetColumnName,
  SuperAdminStatsByConfSheetColumn,
  SuperAdminStatsByConfSheetColumnName,
  SuperAdminStatsByConfSheetRow,
  SuperAdminSmsStatsByConfSheetRow,
  NpsSheetColumn,
  NpsSheetColumnName,
  NpsSheetRow,
  AbTestingRow,
  AbTestingSheetColumn,
  AbTestingSheetColumnName,
  SuperAdminSmsStatsSheetColumn,
  SuperAdminSmsStatsByConfSheetColumnName,
} from '../model';

@Injectable({
  providedIn: 'root',
})
export class ExcelFileExporterService implements FileExporter {
  private workbook: Workbook;

  constructor(
    private readonly translator: TranslatorService,
    private readonly configurationService: ConfigurationService,
    private readonly timezoneService: TimezoneService,
    private readonly featureFlippingService: FeatureFlippingManagerService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {
    this.workbook = new Workbook();
    this.initNewWorkbook();
  }

  private static formatFirstRow(sheet: Worksheet) {
    const firstRow = sheet.getRow(1);

    firstRow.font = {
      bold: true,
    };

    const basicBorder: Partial<Border> = {
      style: 'thin',
      color: { argb: '00000000' },
    };

    firstRow.border = {
      top: basicBorder,
      bottom: basicBorder,
      left: basicBorder,
      right: basicBorder,
    };
  }

  private static formatSheetColumns<T extends AnySheetRow>(
    rawColumns: SpecificSheetColumn<T>[],
    excludeKeys: SpecificSheetColumnName<T>[]
  ): SpecificSheetColumn<T>[] {
    return rawColumns.filter((col) => !excludeKeys.includes(col.key));
  }

  private canCollectMarketingData() {
    return (
      this.featureFlippingService.isFeatureActivated(
        'b2c-field-display-marketing-data'
      ) ||
      this.featureFlippingService.isFeatureActivated(
        'b2b-field-display-marketing-data'
      )
    );
  }

  private getDossierColumns(
    excludeKeys: DossierSheetColumnName[]
  ): DossierSheetColumn[] {
    const columns: DossierSheetColumn[] = [
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.insurer'
        ),
        key: 'insurer',
        width: 25,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.rriNumber'
        ),
        key: 'rriNumber',
        width: 25,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.origin'
        ),
        key: 'origin',
        width: 25,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.ID'
        ),
        key: 'ID',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.creationDate'
        ),
        key: 'creationDate',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.fitterBrandId'
        ),
        key: 'fitterBrandId',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.fitterBrand'
        ),
        key: 'fitterBrand',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.fitterId'
        ),
        key: 'fitterId',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.customerFirstnameContract'
        ),
        key: 'customerFirstnameContract',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.customerLastnameContract'
        ),
        key: 'customerLastnameContract',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.customerEmailContract'
        ),
        key: 'customerEmailContract',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.contractNumber'
        ),
        key: 'contractNumber',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.sinisterDate'
        ),
        key: 'sinisterDate',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.licensePlateNumber'
        ),
        key: 'licensePlateNumber',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.vehicleBrand'
        ),
        key: 'vehicleBrand',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.vehicleModel'
        ),
        key: 'vehicleModel',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.sinisterGlassClaim'
        ),
        key: 'sinisterGlassClaim',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.outsideIntervention'
        ),
        key: 'outsideIntervention',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.replacementVehicle'
        ),
        key: 'replacementVehicle',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.operationType'
        ),
        key: 'operationType',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.conveying'
        ),
        key: 'conveying',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.calibration'
        ),
        key: 'calibration',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.sentToBill'
        ),
        key: 'sentToBill',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.resources'
        ),
        key: 'resources',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.interventionCity'
        ),
        key: 'interventionCity',
        width: 25,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.fitterCity'
        ),
        key: 'fitterCity',
        width: 25,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.customerFirstname'
        ),
        key: 'customerFirstname',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.customerLastname'
        ),
        key: 'customerLastname',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.customerEmail'
        ),
        key: 'customerEmail',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.apptDate'
        ),
        key: 'apptDate',
        width: 25,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.apptHour'
        ),
        key: 'apptHour',
        width: 25,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.agentLastname'
        ),
        key: 'agentLastname',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.agentFirstname'
        ),
        key: 'agentFirstname',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.agentEmail'
        ),
        key: 'agentEmail',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.noShow'
        ),
        key: 'noShow',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.finishLaterComment'
        ),
        key: 'finishLaterComment',
        width: 50,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.abandonmentComment'
        ),
        key: 'abandonmentComment',
        width: 50,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.apmtCancelComment'
        ),
        key: 'apmtCancelComment',
        width: 50,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.abandonDate'
        ),
        key: 'abandonDate',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.globalOrigin'
        ),
        key: 'globalOrigin',
        width: 50,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.businessOrigin'
        ),
        key: 'businessOrigin',
        width: 50,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.phoneNumber'
        ),
        key: 'phoneNumber',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.sinisterNumber'
        ),
        key: 'sinisterNumber',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.marketingDataAccepted'
        ),
        key: 'marketingDataAccepted',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.lastStep'
        ),
        key: 'lastStep',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.dossiers.headers.abTestPhone'
        ),
        key: 'abTestPhone',
        width: 32,
      },
    ];

    return ExcelFileExporterService.formatSheetColumns(
      columns,
      excludeKeys
    ).filter((col) => {
      const displayMarketingCol = this.canCollectMarketingData();

      return col.key !== 'marketingDataAccepted' || displayMarketingCol;
    });
  }

  private getDossierManagersColumns(
    excludeKeys: DossierManagersSheetColumnName[]
  ): DossierManagersSheetColumn[] {
    const columns: DossierManagersSheetColumn[] = [
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.date'
        ),
        key: 'date',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.agentLastname'
        ),
        key: 'agentLastname',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.agentFirstname'
        ),
        key: 'agentFirstname',
        width: 32,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.agentEmail'
        ),
        key: 'agentEmail',
        width: 32,
      },
      // Ajout colonne / fitter
      ...this.configurationService.getConfig().fitterBrands.map((brand) => {
        return {
          header: brand.name.toUpperCase(),
          key: brand.id,
          width: 15,
        };
      }),
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.repair'
        ),
        key: 'repair',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.replace'
        ),
        key: 'replace',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.interventionAtGarage'
        ),
        key: 'interventionAtGarage',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.interventionAtHome'
        ),
        key: 'interventionAtHome',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.requestAtGarage'
        ),
        key: 'requestAtGarage',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.management.headers.requestAtHome'
        ),
        key: 'requestAtHome',
        width: 20,
      },
    ];

    return ExcelFileExporterService.formatSheetColumns(columns, excludeKeys);
  }

  private getPartnersColumns(
    excludeKeys: PartnerSheetColumnName[]
  ): PartnerSheetColumn[] {
    const columns: PartnerSheetColumn[] = [
      {
        key: 'id',
        header: 'id',
        width: 20,
      },
      {
        key: 'internalId',
        header: 'internalId',
        width: 20,
      },
      {
        key: 'siret',
        header: 'siret',
        width: 30,
      },
      {
        key: 'brandId',
        header: 'brandId',
        width: 30,
      },
      {
        key: 'brand',
        header: 'brand',
        width: 30,
      },
      {
        key: 'city',
        header: 'city',
        width: 25,
      },
      {
        key: 'address',
        header: 'address',
        width: 40,
      },
      {
        key: 'cp',
        header: 'cp',
        width: 15,
      },
      {
        key: 'phone',
        header: 'phone',
        width: 20,
      },
      {
        key: 'email',
        header: 'email',
        width: 20,
      },
      {
        key: 'lat',
        header: 'lat',
        width: 10,
        style: { numFmt: '@' },
      },
      {
        key: 'lon',
        header: 'lon',
        width: 10,
        style: { numFmt: '@' },
      },
      {
        key: 'fitterType',
        header: 'fitterType',
        width: 30,
      },
    ];

    return ExcelFileExporterService.formatSheetColumns(columns, excludeKeys);
  }

  private getSmsStatsColumns(): SmsStatsSheetColumn[] {
    const columns: SmsStatsSheetColumn[] = [
      {
        key: 'fitterId',
        header: 'Centre',
        width: 20,
      },
      {
        key: 'nbCustomerSms',
        header: 'SMS Client',
        width: 20,
      },
      {
        key: 'nbFitterSms',
        header: 'SMS Fitter',
        width: 20,
      },
      {
        key: 'total',
        header: 'TOTAL',
        width: 20,
      },
    ];

    return ExcelFileExporterService.formatSheetColumns(columns, []);
  }

  private getSuperAdminSmsStatsByConfColumns(
    excludeKeys: SuperAdminSmsStatsByConfSheetColumnName[]
  ): SuperAdminSmsStatsSheetColumn[] {
    const columns: SuperAdminSmsStatsSheetColumn[] = [
      {
        key: 'from',
        header: 'Etiquette de ligne',
        width: 20,
      },
      {
        key: 'to',
        header: 'Client ou Fitter',
        width: 20,
      },
      {
        key: 'smsType',
        header: 'Type de SMS',
        width: 50,
      },
      {
        key: 'nbSms',
        header: 'SMS',
        width: 20,
      },
      {
        key: 'nbCharacters',
        header: 'nombre de caractères',
        width: 20,
      },
      {
        key: 'nbSmsFactured',
        header: 'nombre de SMS facturés',
        width: 20,
      },
    ];

    return ExcelFileExporterService.formatSheetColumns(columns, excludeKeys);
  }

  private getSuperAdminStatsByConfColumns(
    excludeKeys: SuperAdminStatsByConfSheetColumnName[]
  ): SuperAdminStatsByConfSheetColumn[] {
    const columns: SuperAdminStatsByConfSheetColumn[] = [
      {
        key: 'confId',
        header: 'Client',
        width: 20,
      },
      {
        key: 'nbInsurerApmt',
        header: 'Rdv BtoB',
        width: 20,
      },
      {
        key: 'nbCustomerApmt',
        header: 'Rdv BtoC',
        width: 20,
      },
      {
        key: 'nbActiveInsurerAccounts',
        header: 'Gestionnaires actifs',
        width: 40,
      },
      {
        key: 'nbBrandFitters',
        header: 'Nombre de centres',
        width: 40,
      },
      {
        key: 'nbAppointments',
        header: 'Total des Rdv',
        width: 20,
      },
      {
        key: 'conversionRateB2C',
        header: 'Taux de conversion B2C',
        width: 40,
        style: {
          numFmt: '+0.00%;-0.00%',
        },
      },
      {
        key: 'conversionRateB2B',
        header: 'Taux de conversion B2B',
        width: 40,
        style: {
          numFmt: '+0.00%;-0.00%',
        },
      },
      {
        key: 'repairRate',
        header: 'Taux de réparation',
        width: 40,
        style: {
          numFmt: '+0.00%;-0.00%',
        },
      },
      {
        key: 'variationFactor',
        header: 'Variation des Rdv',
        width: 40,
        style: {
          numFmt: '+0.00%;-0.00%',
        },
      },
    ];

    return ExcelFileExporterService.formatSheetColumns(columns, excludeKeys);
  }

  private getNpsColumns(excludeKeys: NpsSheetColumnName[]): NpsSheetColumn[] {
    const columns: NpsSheetColumn[] = [
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.nps.headers.journeyType'
        ),
        key: 'journeyType',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.nps.headers.clientName'
        ),
        key: 'clientName',
        width: 25,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.nps.headers.dossierNumber'
        ),
        key: 'dossierNumber',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.nps.headers.date'
        ),
        key: 'date',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.nps.headers.result'
        ),
        key: 'result',
        width: 15,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.nps.headers.filledFields'
        ),
        key: 'filledFields',
        width: 50,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.nps.headers.userSkipped'
        ),
        key: 'userSkipped',
        width: 20,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.nps.headers.appOrigin'
        ),
        key: 'appOrigin',
        width: 20,
      },
    ];

    return ExcelFileExporterService.formatSheetColumns(columns, excludeKeys);
  }

  private getAbTestingColumns(
    excludeKeys: AbTestingSheetColumnName[]
  ): AbTestingSheetColumn[] {
    const columns: AbTestingSheetColumn[] = [
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.abTesting.headers.testType'
        ),
        key: 'testType',
        width: 70,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.abTesting.headers.nbDossiersA'
        ),
        key: 'nbDossiersA',
        width: 45,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.abTesting.headers.nbDossiersB'
        ),
        key: 'nbDossiersB',
        width: 45,
      },
      {
        header: this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.exports.excel.abTesting.headers.rate'
        ),
        key: 'rate',
        width: 20,
      },
    ];

    return ExcelFileExporterService.formatSheetColumns(columns, excludeKeys);
  }

  private addSheet<T extends AnySheetRow>(
    sheetName: string,
    columns: SpecificSheetColumn<T>[],
    sheetData: T[]
  ) {
    const sheet = this.workbook.addWorksheet(sheetName);

    sheet.columns = columns;
    sheet.addRows(sheetData);

    ExcelFileExporterService.formatFirstRow(sheet);
  }

  public initNewWorkbook() {
    this.workbook = new Workbook();
    this.workbook.creator = 'Auto';
    this.workbook.lastModifiedBy = 'Auto';
    this.workbook.created = new Date();
    this.workbook.modified = new Date();
    this.workbook.lastPrinted = new Date();
    this.workbook.properties.date1904 = true;
  }

  public addDossiersPage(
    sheetName: string,
    dossiers: Dossier[],
    fitters: Fitter[] = [],
    excludeKeys: DossierSheetColumnName[] = []
  ) {
    const dossierRowsData: DossierSheetRow[] = dossiers.map((dossier) => {
      const fitter = fitters?.find(
        (_fitter) =>
          _fitter.id ===
          (dossier?.appointment?.fitterId ||
            dossier?.requestAppointment?.fitterId)
      );

      const currentTimezone = dossier.appointment
        ? this.timezoneService.getTimezone(dossier.appointment)
        : this.timezoneService.getDefaultTimezone();

      const abandonDate = dossier?.stateHistory?.find(
        (state) => state?.state === 'ABANDONED'
      )?.timestamp;

      // Populating AgendInfo (Default is what's come from insurerData structure on the dossier)
      let agentInfo = {
        agentLastname: dossier.insurerData?.callAgentLastname,
        agentFirstname: dossier.insurerData?.callAgentFirstname,
        agentEmail: dossier.insurerData?.callAgent,
      };
      if (dossier.creationData?.origin === 'B2B') {
        // If the agent info needs to be from the last Updater (By default insurerData structure is fill with the creator of the dossier or first updater)
        if (
          !agentInfo.agentLastname ||
          this.featureFlippingService.isFeatureActivated(
            'b2b-populate-agents-info-from-first-editor'
          )
        ) {
          agentInfo = this.getAgentInfoFromDossierStates(dossier);
        }
      }

      if (
        !this.featureFlippingService.isFeatureActivated(
          'b2c-get-customer-number-earlier'
        )
      ) {
        excludeKeys.push('abTestPhone');
      }

      const operationType = dossier.claim?.operationType
        ? dossier.claim?.operationType
        : dossier?.claim?.glass
        ? getOperationType(dossier?.claim?.glass)
        : null;

      const newRowValues: DossierSheetRow = {
        ID: dossier.id,
        rriNumber: dossier.insurerData?.rriNumber,
        fitterBrandId:
          dossier.appointment?.brandId ?? dossier.requestAppointment?.brandId,
        fitterBrand: fitter?.brand,
        fitterId:
          dossier.appointment?.fitterId ?? dossier.requestAppointment?.fitterId,
        creationDate: dossier.createDate
          ? this.timezoneService.getExcelLocalDateTimeFormat(
              dossier.createDate,
              currentTimezone
            )
          : null,
        customerLastname: dossier.customer?.lastname,
        customerFirstname: dossier.customer?.firstname,
        customerEmail: dossier.customer?.email,
        sinisterDate: dayjs(dossier?.claim?.sinisterDate)?.isValid()
          ? dayjs(dossier?.claim?.sinisterDate)
              ?.tz(currentTimezone)
              ?.format('DD-MM-YYYY')
          : null,
        licensePlateNumber: dossier.claim?.car?.licensePlateNumber,
        vehicleBrand: dossier.claim?.car?.manufacturer,
        vehicleModel: dossier.claim?.car?.model,
        sinisterGlassClaim: dossier.claim?.glass?.glass
          ? this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.sinisterGlass.glass.' +
                dossier.claim?.glass?.glass +
                '.name'
            )
          : '--',
        contractNumber: dossier.policyInformation?.policyNumber,
        insurer: dossier.policyInformation?.insuranceName,
        origin: this.translator.getInstantTranslation(
          `GlobalApplicativeConstants.DossierOrigins.${this.configurationService.getDossierGlobalOriginFromCreationData(
            dossier?.creationData,
            this.clientAppId
          )}`
        ),
        customerLastnameContract: dossier.policyInformation?.insuredLastname,
        customerFirstnameContract: dossier.policyInformation?.insuredFirstname,
        customerEmailContract: dossier.policyInformation?.insuredEmail,
        outsideIntervention:
          dossier.appointment?.outsideIntervention ??
          dossier.requestAppointment?.outsideIntervention
            ? this.translator.getInstantTranslation(
                'GlobalApplicativeConstants.action.yes'
              )
            : this.translator.getInstantTranslation(
                'GlobalApplicativeConstants.action.no'
              ),
        replacementVehicle:
          dossier.appointment?.isReplacementVehicleSelected ??
          dossier.requestAppointment?.isReplacementVehicleSelected
            ? this.translator.getInstantTranslation(
                'GlobalApplicativeConstants.action.yes'
              )
            : this.translator.getInstantTranslation(
                'GlobalApplicativeConstants.action.no'
              ),
        operationType:
          operationType === 'Repair'
            ? this.translator.getInstantTranslation(
                'GlobalApplicativeConstants.operationType.repairTitle'
              )
            : operationType === 'Replace'
            ? this.translator.getInstantTranslation(
                'GlobalApplicativeConstants.operationType.replaceTitle'
              )
            : '--',
        conveying: dossier.appointment?.isConveying
          ? this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.yes'
            )
          : this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.no'
            ),
        calibration: this.getCalibration(dossier.claim?.calibration),
        sentToBill: dossier.fitterInfo?.isReadyToBill
          ? this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.yes'
            )
          : this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.no'
            ),
        resources: dossier.appointment?.assignedResources?.join(','),
        interventionCity:
          dossier.appointment?.interventionLocation?.addressElements?.city ??
          dossier.requestAppointment?.interventionLocation?.addressElements
            ?.city,
        fitterCity: fitter?.city,
        apptDate: dossier.appointment?.time
          ? dayjs(dossier.appointment?.time)
              .tz(currentTimezone)
              .format('DD-MM-YYYY')
          : '',
        apptHour: dossier.appointment?.time
          ? dayjs(dossier.appointment?.time).tz(currentTimezone).format('HH:mm')
          : '',
        agentLastname: agentInfo?.agentLastname,
        agentFirstname: agentInfo?.agentFirstname,
        agentEmail: agentInfo?.agentEmail,
        finishLaterComment: dossier.insurerData?.finishLaterComment,
        abandonmentComment: dossier.insurerData?.abandonmentComment,
        apmtCancelComment: dossier.appointment?.comment,
        noShow: dossier.appointment?.status === 'NO_SHOW',
        abandonDate: abandonDate
          ? dayjs(abandonDate).tz(currentTimezone).format('DD-MM-YYYY')
          : '',
        globalOrigin: this.translator.getInstantTranslation(
          `Dashboard.appointmentsOrigin.${this.configurationService.getDossierGlobalOriginFromCreationData(
            dossier.creationData,
            this.clientAppId
          )}`
        ),
        businessOrigin: dossier.creationData?.businessOrigin,
        phoneNumber: dossier.customer?.phoneNumber?.number,
        sinisterNumber: dossier.policyInformation?.sinisterNumber,
        marketingDataAccepted: dossier.marketingData?.accepted
          ? this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.yes'
            )
          : this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.no'
            ),
        lastStep: this.getDisplayStep(dossier),
        abTestPhone: dossier.abTesting?.find(
          (abTest) => abTest.abTestType === 'b2c-early-customer-phone-number'
        )?.value
          ? this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.yes'
            )
          : this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.no'
            ),
      };

      excludeKeys.forEach((key) => delete newRowValues[key]);

      return newRowValues;
    });

    this.addSheet(
      sheetName,
      this.getDossierColumns(excludeKeys),
      dossierRowsData
    );
  }

  public getAgentInfoFromDossierStates(dossier: Dossier) {
    // the same method exist for backend inside dossier-utils
    const orderedState = (dossier.stateHistory || []).filter(
      (s) =>
        s.userName &&
        s.userName.length >= 1 &&
        s.userType === 'INSURER' &&
        !s.userId?.includes('@aari.io')
    );
    let filteredState = orderedState?.filter((s) => s.state === 'INIT');
    let userName;
    let email;
    if (filteredState.length > 0) {
      userName = filteredState[filteredState.length - 1].userName;
      email = filteredState[filteredState.length - 1].userId;
    } else {
      filteredState = orderedState?.filter((s) => s.state === 'QUALIFIED');
      if (filteredState.length > 0) {
        userName = filteredState[filteredState.length - 1].userName;
        email = filteredState[filteredState.length - 1].userId;
      } else {
        filteredState = orderedState?.filter((s) => s.state === 'COMPLETE');
        if (filteredState.length > 0) {
          userName = filteredState[filteredState.length - 1].userName;
          email = filteredState[filteredState.length - 1].userId;
        } else {
          filteredState = orderedState?.filter((s) => s.state === 'ABANDONED');
          if (filteredState.length > 0) {
            userName = filteredState[filteredState.length - 1].userName;
            email = filteredState[filteredState.length - 1].userId;
          }
        }
      }
    }
    if (
      userName &&
      userName.length >= 7 &&
      userName.substring(userName.length - 7, userName.length - 6) === ' '
    ) {
      // Specific rule for matricule (Macif)
      return {
        agentFirstname: userName.substring(0, userName.length - 7),
        agentLastname: userName.substring(userName.length - 6, userName.length),
        agentEmail: email,
      };
    }
    return {
      agentLastname: userName,
      agentFirstname: '',
      agentEmail: email,
    };
  }

  public addDossiersManagersPage(
    sheetName: string,
    dossiers: Dossier[],
    excludeKeys: DossierManagersSheetColumnName[] = []
  ) {
    const dossiersManagersRowsData: DossierManagersSheetRow[] = [];

    const dossiersGroupedByAgent: Record<string, Dossier[]> = {};
    const hasRriNumberFeature = this.featureFlippingService.isFeatureActivated(
      'can-manage-rri-number'
    );

    dossiers
      .filter(
        (dossier) =>
          dossier.state !== 'ABANDONED' &&
          (!!dossier.appointment?.brandId ||
            !!dossier.requestAppointment?.brandId)
      )
      .forEach((d) => {
        if (hasRriNumberFeature) {
          if (!d.insurerData?.rriNumber) {
            return;
          }

          if (!dossiersGroupedByAgent[d.insurerData.rriNumber]) {
            dossiersGroupedByAgent[d.insurerData.rriNumber] = [];
          }

          dossiersGroupedByAgent[d.insurerData.rriNumber].push(d);
        } else {
          if (!d.insurerData?.callAgent) {
            return;
          }

          if (!dossiersGroupedByAgent[d.insurerData.callAgent]) {
            dossiersGroupedByAgent[d.insurerData.callAgent] = [];
          }

          dossiersGroupedByAgent[d.insurerData.callAgent].push(d);
        }
      });

    Object.keys(dossiersGroupedByAgent).forEach((agent) => {
      const dossiersGroupedByDate: Record<string, Dossier[]> = {};

      dossiersGroupedByAgent[agent].forEach((d) => {
        if (!d.createDate) {
          return;
        }

        const usedDate = dayjs(d.createDate)
          ?.tz(this.timezoneService.getDefaultTimezone())
          .format('YYYY-MM-DD');

        if (!dossiersGroupedByDate[usedDate]) {
          dossiersGroupedByDate[usedDate] = [];
        }

        dossiersGroupedByDate[usedDate].push(d);
      });

      Object.keys(dossiersGroupedByDate).forEach((date) => {
        let nbInCenter = 0;
        let nbOutside = 0;
        let nbRequestInCenter = 0;
        let nbRequestOutside = 0;
        const nbSinisterByOperationType: Record<OperationType, number> = {
          Repair: 0,
          Replace: 0,
        };
        const nbDossierByFitter: Record<string, number> = {};

        if (dossiersGroupedByDate[date].length > 0) {
          dossiersGroupedByDate[date].forEach((d) => {
            const isRequestApmt = !d.appointment && !!d.requestAppointment;

            if (isRequestApmt) {
              if (d.requestAppointment?.outsideIntervention) {
                nbRequestOutside++;
              } else {
                nbRequestInCenter++;
              }
            } else {
              if (d.appointment?.outsideIntervention) {
                nbOutside++;
              } else {
                nbInCenter++;
              }
            }

            if (d.claim?.operationType === 'Repair') {
              nbSinisterByOperationType['Repair']++;
            } else {
              nbSinisterByOperationType['Replace']++;
            }

            if (
              !!d.appointment?.brandId ||
              (!d.appointment?.brandId && !!d.requestAppointment?.brandId)
            ) {
              const brandId =
                d.appointment?.brandId ?? d.requestAppointment?.brandId ?? '';
              if (!nbDossierByFitter[brandId]) {
                nbDossierByFitter[brandId] = 0;
              }

              nbDossierByFitter[brandId]++;
            }
          });

          const newRow = {
            agentLastname:
              dossiersGroupedByDate[date][0].insurerData?.callAgentLastname,
            agentFirstname:
              dossiersGroupedByDate[date][0].insurerData?.callAgentFirstname,
            agentEmail: hasRriNumberFeature
              ? dossiersGroupedByDate[date][0].insurerData?.rriNumber ?? ''
              : dossiersGroupedByDate[date][0].insurerData?.callAgent,
            date: date,
            ...nbDossierByFitter,
            repair: nbSinisterByOperationType.Repair,
            replace: nbSinisterByOperationType.Replace,
            interventionAtGarage: nbInCenter,
            interventionAtHome: nbOutside,
            requestAtGarage: nbRequestInCenter,
            requestAtHome: nbRequestOutside,
          } as DossierManagersSheetRow;
          dossiersManagersRowsData.push(newRow);
        }
      });
    });

    this.addSheet(
      sheetName,
      this.getDossierManagersColumns(excludeKeys),
      dossiersManagersRowsData
    );
  }

  public addPartnerPage(
    sheetName: string,
    partners: Fitter[],
    excludeKeys: PartnerSheetColumnName[] = []
  ) {
    const partnerRowsData: PartnerSheetRow[] = partners.map((partner) => ({
      id: partner.id,
      internalId: partner.internalId,
      siret: partner.siret,
      brand: partner.brand,
      city: partner.city,
      brandId: partner.brandId,
      address: partner.address,
      cp: partner.cp,
      phone: partner.phone,
      email: partner.email,
      lat: partner?.location?.lat,
      lon: partner?.location?.lon,
      fitterType: partner?.fitterType,
    }));

    this.addSheet(
      sheetName,
      this.getPartnersColumns(excludeKeys),
      partnerRowsData
    );
  }

  public addSmsStatsPage(sheetName: string, smsStats: SmsStatsSheetRow[]) {
    this.addSheet(sheetName, this.getSmsStatsColumns(), smsStats);
  }

  public addSuperAdminSmsStatsByConfPage(
    sheetName: string,
    smsStats: SuperAdminSmsStatsByConfSheetRow[],
    excludeKeys: SuperAdminSmsStatsByConfSheetColumnName[] = []
  ) {
    this.addSheet(
      sheetName,
      this.getSuperAdminSmsStatsByConfColumns(excludeKeys),
      smsStats
    );
  }

  public addSuperAdminStatsByConfPage(
    sheetName: string,
    confStats: SuperAdminStatsByConfSheetRow[],
    excludeKeys: SuperAdminStatsByConfSheetColumnName[] = []
  ) {
    const displayNbFitters = confStats.some(
      (confStat) => !!confStat?.nbBrandFitters
    );

    const usedExcludeKeys: SuperAdminStatsByConfSheetColumnName[] = [
      ...excludeKeys,
    ];

    if (!displayNbFitters) {
      usedExcludeKeys.push('nbBrandFitters');
    }

    this.addSheet(
      sheetName,
      this.getSuperAdminStatsByConfColumns(usedExcludeKeys),
      confStats
    );
  }

  public addNpsPage(sheetName: string, dossiers: Dossier[]) {
    const npsRowsData: NpsSheetRow[] = [];

    // Filter dossiers that have NPS data (where nps array exists and has entries)
    const dossiersWithNps = dossiers.filter(
      (dossier) => dossier.nps && dossier.nps.length > 0
    );

    dossiersWithNps.forEach((dossier) => {
      // Process each NPS entry in the dossier's nps array
      dossier.nps?.forEach((npsEntry) => {
        const currentTimezone = dossier.appointment
          ? this.timezoneService.getTimezone(dossier.appointment)
          : this.timezoneService.getDefaultTimezone();

        // Determine the type of journey (B2B/B2C)
        const journeyType =
          dossier.creationData?.origin === 'B2B' ? 'B2B' : 'B2C';

        // Get client name from insurer information
        const clientName =
          this.configurationService.getConfig().insurerName ||
          this.translator.getInstantTranslation(
            'GlobalApplicativeConstants.action.notSpecified'
          );

        const npsRow: NpsSheetRow = {
          journeyType: journeyType,
          clientName: clientName,
          dossierNumber: dossier.id,
          date: npsEntry.timestamp
            ? dayjs(npsEntry.timestamp).tz(currentTimezone).format('DD-MM-YYYY')
            : dossier.createDate
            ? dayjs(dossier.createDate).tz(currentTimezone).format('DD-MM-YYYY')
            : null,
          result: npsEntry.score,
          filledFields: npsEntry.comment || '',
          userSkipped: npsEntry.userSkipped
            ? this.translator.getInstantTranslation(
                'GlobalApplicativeConstants.action.yes'
              )
            : this.translator.getInstantTranslation(
                'GlobalApplicativeConstants.action.no'
              ),
          appOrigin:
            npsEntry.appOrigin ||
            this.translator.getInstantTranslation(
              'GlobalApplicativeConstants.action.notSpecified'
            ),
        };

        npsRowsData.push(npsRow);
      });
    });

    this.addSheet(sheetName, this.getNpsColumns([]), npsRowsData);
  }

  public addAbTestingPage(sheetName: string, dossiers: Dossier[]) {
    const abTestingRowsData: AbTestingRow[] = [];

    dossiers.forEach((dossier) => {
      if (dossier.abTesting && dossier?.abTesting?.length > 0) {
        dossier.abTesting?.forEach((abTestingEntry) => {
          const abTestingDataIndex = abTestingRowsData.findIndex(
            (abTestingRow) =>
              abTestingRow.testType === abTestingEntry.abTestType
          );
          let abTestingData = abTestingRowsData[abTestingDataIndex];
          if (!abTestingData) {
            abTestingData = {
              testType: abTestingEntry.abTestType,
              nbDossiersA: 0,
              nbDossiersB: 0,
              rate: 0,
            };
          }

          if (abTestingEntry.value === true) {
            abTestingData.nbDossiersA += 1;
          } else {
            abTestingData.nbDossiersB += 1;
          }
          if (abTestingDataIndex === -1) {
            abTestingRowsData.push(abTestingData);
          } else {
            abTestingRowsData[abTestingDataIndex] = abTestingData;
          }
        });
      }
    });

    abTestingRowsData.forEach((abTestingData) => {
      const total = abTestingData.nbDossiersA + abTestingData.nbDossiersB;
      abTestingData.rate =
        total > 0
          ? Number.parseFloat(
              ((abTestingData.nbDossiersA / total) * 100).toFixed(1)
            )
          : 0;
      abTestingData.testType = this.translator.getInstantTranslation(
        `GlobalApplicativeConstants.exports.excel.abTesting.headers.testTypes.${abTestingData.testType}`
      );
    });

    this.addSheet(sheetName, this.getAbTestingColumns([]), abTestingRowsData);
  }

  public exportTableViewCallback(callback: (workbook: Workbook) => void): void {
    callback(this.workbook);
  }

  public autoFitColumns(worksheet: Worksheet, minimalWidth = 2) {
    worksheet.columns.forEach((column) => {
      let maxColumnLength = 0;
      column.eachCell?.({ includeEmpty: false }, (cell, rowNumber) => {
        if (rowNumber <= 1) {
          return;
        }
        maxColumnLength = Math.max(
          maxColumnLength,
          minimalWidth,
          cell.value ? cell.value.toString().length : 0
        );
      });
      column.width = maxColumnLength + 10;
    });
  }

  getCalibration(calibration: CalibrationType | undefined) {
    switch (calibration) {
      case 'true':
        return this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.action.yes'
        );
      case 'false':
        return this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.action.no'
        );
      default:
        return this.translator.getInstantTranslation(
          'GlobalApplicativeConstants.action.notSpecified'
        );
    }
  }

  getDisplayStep(dossier: Dossier) {
    const stepKey = this.configurationService
      ?.getConfig()
      ?.stepperPageMapping?.find((step) =>
        step?.pages.includes(dossier?.declarationPage as RoutingName)
      )?.stepKey;
    const steptrad = this.translator.getInstantTranslation(
      `GlobalApplicativeConstants.StepperTitles.CustomerStepper.${stepKey}`
    );
    const pageName = this.translator?.getInstantTranslation(
      `GlobalApplicativeConstants.StepperTitles.pageTitles.customer.${dossier?.declarationPage}`
    );

    return !!stepKey && !!dossier?.declarationPage
      ? steptrad + ' - ' + pageName
      : '';
  }

  public async exportFile(fileName: string) {
    const usedFileName =
      fileName?.length > 0
        ? fileName.charAt(0) === '.'
          ? `index${fileName}`
          : fileName
        : 'index.xlsx';
    const fileFormatUsed = usedFileName.split('.').pop() || 'xlsx';
    let workbookBuffer: Buffer;
    switch (fileFormatUsed) {
      case 'csv':
        workbookBuffer = await this.workbook.csv.writeBuffer();
        break;
      case 'xlsx':
      default:
        workbookBuffer = await this.workbook.xlsx.writeBuffer();
        break;
    }

    const workbookFile = new File([workbookBuffer], usedFileName);
    saveAs(workbookFile);
  }
}
